import { useEffect, useState } from 'react';
import { MOBILE_BREAKPOINT } from '@/constants/mobile-breakpoints';

/**
 * Detects if the actual device is mobile (regardless of "Request Desktop Site" setting)
 * This checks User Agent, touch capability, and actual screen size
 */
const getIsMobileDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  // Check User Agent for mobile devices
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const mobileRegex =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;

  // Check for touch capability (most mobile devices have touch)
  const hasTouch =
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0;

  // Check actual screen dimensions (not viewport - this doesn't change with "Desktop Site")
  const isSmallScreen =
    window.screen.width <= 768 || window.screen.height <= 768;

  return mobileRegex.test(userAgent) || (hasTouch && isSmallScreen);
};

// Get initial mobile state synchronously to avoid flash/API race conditions
const getInitialMobileState = (breakpoint, detectDevice) => {
  if (typeof window === 'undefined') return false;
  if (detectDevice) return getIsMobileDevice();
  return window.innerWidth <= breakpoint;
};

export const useIsMobile = ({
  mobileBreakpoint = MOBILE_BREAKPOINT,
  detectDevice = false,
} = {}) => {
  // Initialize with correct value immediately to prevent API race conditions
  const [isMobile, setIsMobile] = useState(() =>
    getInitialMobileState(mobileBreakpoint, detectDevice)
  );

  useEffect(() => {
    const handleResize = () => {
      // If detectDevice is true, always use device detection (ignores viewport changes)
      const isNowMobile = detectDevice
        ? getIsMobileDevice()
        : window.innerWidth <= mobileBreakpoint;

      setIsMobile((prev) => (prev !== isNowMobile ? isNowMobile : prev));
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [mobileBreakpoint, detectDevice]);

  return isMobile;
};

/**
 * Hook that always detects actual mobile device (ignores "Request Desktop Site")
 * Use this when mobile view MUST always show on mobile devices
 */
export const useIsMobileDevice = () => {
  const [isMobileDevice, setIsMobileDevice] = useState(() => getIsMobileDevice());

  useEffect(() => {
    setIsMobileDevice(getIsMobileDevice());
  }, []);

  return isMobileDevice;
};
